'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { SessionContext } from '@benzinga/session-context';
import { TrackingManager } from '@benzinga/tracking-manager';
import type { SubscriptionsManager, UserSubscription } from '@benzinga/subscription-manager';
import type { ProductFamily, SubscriptionRowModel, SubscriptionTag } from './subscriptions';
import { ToggleSwitch } from '../_shell/ToggleSwitch';
import styles from './Billing.module.css';

const TAG_COPY: Record<SubscriptionTag, { key: string; label: string }> = {
  'auto-renew': { key: 'Billing.tag-auto-renew', label: 'AUTO-RENEW' },
  cancels: { key: 'Billing.tag-cancels', label: 'CANCELING' },
  ended: { key: 'Billing.tag-ended', label: 'ENDED' },
  exclusive: { key: 'Billing.tag-exclusive', label: 'EXCLUSIVE' },
  paused: { key: 'Billing.tag-paused', label: 'PAUSED' },
  trial: { key: 'Billing.tag-trial', label: 'TRIAL' },
  upgrading: { key: 'Billing.tag-upgrading', label: 'UPGRADING' },
};

const TAG_CLASS: Partial<Record<SubscriptionTag, string>> = {
  cancels: styles.tagCancels,
  paused: styles.tagPaused,
  trial: styles.tagTrial,
  upgrading: styles.tagUpgrading,
};

const FAMILY_COPY: Record<ProductFamily, string> = { edge: 'EDGE', pro: 'PRO', research: 'RESEARCH' };

export interface SubscriptionRowProps {
  row: SubscriptionRowModel;
  setSubToCancel: (sub: UserSubscription | null) => void;
  subscriptionManager: SubscriptionsManager;
}

export function SubscriptionRow({ row, setSubToCancel, subscriptionManager }: SubscriptionRowProps) {
  const { t } = useTranslation(['profile', 'common']);
  const session = React.useContext(SessionContext);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const { subscription } = row;

  const trackEvent = (event: 'cancelled' | 'renewed'): void => {
    session.getManager(TrackingManager).trackSubscriptionEvent(event, {
      interval: subscription.interval,
      package_id: subscription.uuid,
      price: subscription.amount,
      product_id: subscription.product,
    });
  };

  const handleRestart = async (): Promise<void> => {
    try {
      const res = await subscriptionManager.restartSubscription(subscription.uuid);
      if (res.err) {
        throw await (res.err?.data as Response)?.json();
      }
      trackEvent('renewed');
    } catch (error) {
      console.error('Error restarting subscription', error);
      toast.error('Unable to update your subscription. Please contact our support team.');
    }
  };

  const handleEndTrial = async (): Promise<void> => {
    setIsUpdating(true);
    try {
      const res = await subscriptionManager.deleteSubscription(subscription.uuid);
      if (res.err) {
        throw await (res.err?.data as Response)?.json();
      }
      trackEvent('cancelled');
    } catch (error) {
      console.error('Error ending trial subscription', error);
      toast.error('Unable to cancel your trial subscription. Please contact our support team.');
    }
    setIsUpdating(false);
  };

  const handleToggle = async (): Promise<void> => {
    setIsUpdating(true);
    // Legacy semantics: turning auto-renew off hands over to the cancel modal.
    if (subscription.cancelAtPeriodEnd === false) {
      setSubToCancel(subscription);
    } else {
      await handleRestart();
    }
    setIsUpdating(false);
  };

  const tag = TAG_COPY[row.tag];

  return (
    <div className={styles.subRow}>
      <div className={styles.subMain}>
        <div className={styles.subTitle}>
          {subscription.planName}
          {row.family ? <span className={`acct-pill acct-pill--${row.family}`}>{FAMILY_COPY[row.family]}</span> : null}
        </div>
        <div className={styles.subMeta}>
          {row.priceLabel ? <span>{row.priceLabel}</span> : null}
          {row.strikePrice ? <span className={styles.strike}>{row.strikePrice}</span> : null}
          <span className={row.dateTone === 'warning' ? styles.subMetaWarning : undefined}>
            {row.dateText ? `${row.dateLabel} ${row.dateText}` : row.dateLabel}
          </span>
          {row.couponLabel ? <span>{row.couponLabel}</span> : null}
          {!row.family && row.productName ? <span>{row.productName}</span> : null}
          {row.parentPlanName ? (
            <span>
              {t('Billing.upgrade-from', { defaultValue: 'Upgrade from', ns: 'profile' })} {row.parentPlanName}
            </span>
          ) : null}
          {row.resumesOn ? (
            <span>
              {t('Billing.resumes-on', { defaultValue: 'Resumes on', ns: 'profile' })} {row.resumesOn}
            </span>
          ) : null}
        </div>
      </div>

      <div className={styles.subRight}>
        <span className={`${styles.tag} ${TAG_CLASS[row.tag] ?? ''}`}>
          {t(tag.key, { defaultValue: tag.label, ns: 'profile' })}
        </span>

        {row.autoRenew !== null ? (
          <span className={styles.switchGroup}>
            <span className={styles.switchLabel}>
              {t('Billing.auto-renew', { defaultValue: 'Auto-renew', ns: 'profile' })}
            </span>
            <ToggleSwitch
              ariaLabel={t('Billing.auto-renew-for', {
                defaultValue: 'Auto-renew for {{planName}}',
                ns: 'profile',
                planName: subscription.planName,
              })}
              checked={row.autoRenew}
              disabled={isUpdating}
              id={subscription.uuid}
              onClick={handleToggle}
            />
          </span>
        ) : null}

        {row.canCancelTrial ? (
          <button
            className={`${styles.btnSecondary} ${styles.btnDanger}`}
            disabled={isUpdating}
            onClick={handleEndTrial}
            type="button"
          >
            {t('Billing.cancel-trial', { defaultValue: 'Cancel trial', ns: 'profile' })}
          </button>
        ) : null}
      </div>
    </div>
  );
}
